import { createNDJSONStreamWorker } from '../workers/ndjsonStream.worker';
import { api, getApiBaseUrl, getAuthToken } from './http';
import { BATCH_SIZE, FLUSH_MS } from './streaming';
import type { SqlStreamController } from './streaming';

export type DccOldArchiveRow = {
  file_name?: string | null;
  path?: string | null;
  type?: string | null;
  size?: any;
  date_created?: any;
  date_modified?: any;
};

export type DccOldArchiveSearchParams = {
  file_name_q?: string;
  path_q?: string;
  type?: string[];
  created_from?: string;
  created_to?: string;
  modified_from?: string;
  modified_to?: string;
  order_by?: string;
  limit?: number;
  offset?: number;
};

export type DccOldArchiveSearchResponse = {
  rows: DccOldArchiveRow[];
  limit: number;
  offset: number;
  count: number;
  next_offset: number;
  has_more?: boolean;
};

export const fetchDccOldArchiveSearch = async (
  params: DccOldArchiveSearchParams
): Promise<DccOldArchiveSearchResponse> => {
  const response = await api.get('/dbExchange/dcc_old_archive/search', {
    params,
    paramsSerializer: {
      // Ensure ?type=a&type=b for arrays
      indexes: null as any,
    } as any,
  });
  return response.data;
};

export type DccOldArchiveSearchStreamOptions = {
  params: DccOldArchiveSearchParams & { chunk_size?: number };
  batchSize?: number;
  flushMs?: number;
  headers?: Record<string, string>;
  onBatch?: (rows: DccOldArchiveRow[]) => void;
  onDone?: () => void;
  onError?: (error: any) => void;
  signal?: AbortSignal;
  flowControl?: boolean;
};

export const fetchDccOldArchiveSearchStreaming = (
  opts: DccOldArchiveSearchStreamOptions
): SqlStreamController & { ready?: () => void } => {
  const base = getApiBaseUrl();
  const params = new URLSearchParams();
  const p = opts?.params || {};
  if (p.file_name_q) params.set('file_name_q', String(p.file_name_q));
  if (p.path_q) params.set('path_q', String(p.path_q));
  if (Array.isArray(p.type)) {
    p.type.forEach(t => {
      if (t) params.append('type', String(t));
    });
  }
  if (p.created_from) params.set('created_from', String(p.created_from));
  if (p.created_to) params.set('created_to', String(p.created_to));
  if (p.modified_from) params.set('modified_from', String(p.modified_from));
  if (p.modified_to) params.set('modified_to', String(p.modified_to));
  if (p.order_by) params.set('order_by', String(p.order_by));
  if (typeof p.limit === 'number' && isFinite(p.limit)) params.set('limit', String(p.limit));
  if (typeof p.offset === 'number' && isFinite(p.offset)) params.set('offset', String(p.offset));
  if (typeof (p as any).chunk_size === 'number' && isFinite((p as any).chunk_size)) {
    params.set('chunk_size', String((p as any).chunk_size));
  }

  const url = `${base}/dbExchange/dcc_old_archive/search_stream?${params.toString()}`;
  const token = getAuthToken();
  const headers: Record<string, string> = {
    Accept: 'application/x-ndjson',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(opts.headers || {}),
  };

  const worker = createNDJSONStreamWorker();
  const cleanup = () => {
    try {
      worker.terminate();
    } catch {}
  };
  worker.onmessage = (evt: MessageEvent<any>) => {
    const msg = evt.data || {};
    if (msg.type === 'batch' && Array.isArray(msg.rows)) {
      opts.onBatch?.(msg.rows);
    } else if (msg.type === 'done') {
      opts.onDone?.();
      cleanup();
    } else if (msg.type === 'error') {
      opts.onError?.(msg.error || new Error('stream error'));
      cleanup();
    } else if (msg.type === 'aborted') {
      cleanup();
    }
  };

  worker.postMessage({
    type: 'start',
    url,
    headers,
    batchSize: typeof opts.batchSize === 'number' ? opts.batchSize : BATCH_SIZE,
    flushMs: typeof opts.flushMs === 'number' ? opts.flushMs : FLUSH_MS,
    flowControl: !!opts.flowControl,
  });

  if (opts.signal) {
    const onAbort = () => {
      try {
        worker.postMessage({ type: 'abort' });
      } catch {}
      cleanup();
    };
    if (opts.signal.aborted) onAbort();
    else opts.signal.addEventListener('abort', onAbort, { once: true });
  }

  const controller: SqlStreamController & { ready?: () => void } = {
    abort: () => {
      try {
        worker.postMessage({ type: 'abort' });
      } catch {}
      cleanup();
    },
    ready: () => {
      try {
        worker.postMessage({ type: 'ready' });
      } catch {}
    },
  };
  return controller;
};

export const fetchDccOldArchiveTypes = async (params?: {
  q?: string;
  limit?: number;
  file_name_q?: string;
  path_q?: string;
  created_from?: string;
  created_to?: string;
  modified_from?: string;
  modified_to?: string;
}): Promise<{ types: string[] }> => {
  const response = await api.get('/dbExchange/dcc_old_archive/types', {
    params: {
      q: (params?.q || '').trim() || undefined,
      limit: params?.limit,
      file_name_q: (params?.file_name_q || '').trim() || undefined,
      path_q: (params?.path_q || '').trim() || undefined,
      created_from: params?.created_from || undefined,
      created_to: params?.created_to || undefined,
      modified_from: params?.modified_from || undefined,
      modified_to: params?.modified_to || undefined,
    },
    timeout: 15000,
  });
  return response.data;
};
